import React, { useEffect, useState, useCallback } from "react";
import Layout from "../components/Layout";
import client from "../lib/api";
import { Panel } from "../components/ui-kit";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { MediaPicker } from "../components/MediaPicker";
import { FreeKillSwitch } from "../components/FreeKillSwitch";

const ORDER_TYPES = [
  ["buy_at", "Buy At"], ["buy_above", "Buy Above"], ["buy_between", "Buy Between"],
  ["sell_at", "Sell At"], ["sell_below", "Sell Below"], ["sell_between", "Sell Between"],
];
const isZone = (ot) => ot.endsWith("_between");

const renderTg = (txt) =>
  (txt || "").replace(/&amp;/g, "&")
    .split(/(<\/?[bi]>)/).reduce((acc, part) => {
      if (part === "<b>") { acc.b = true; return acc; }
      if (part === "</b>") { acc.b = false; return acc; }
      if (part === "<i>") { acc.i = true; return acc; }
      if (part === "</i>") { acc.i = false; return acc; }
      if (part) acc.nodes.push({ t: part, b: acc.b, i: acc.i });
      return acc;
    }, { nodes: [], b: false, i: false }).nodes;

const Bubble = ({ title, text, buttons, testid }) => (
  <div className="surface p-4" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">{title}</div>
    <div className="bg-[#0f1621] border border-[color:var(--border-subtle)] rounded-lg p-3 text-sm text-slate-100 whitespace-pre-wrap leading-relaxed min-h-[80px]">
      {renderTg(text).map((n, i) => (
        <span key={i} className={`${n.b ? "font-bold" : ""} ${n.i ? "italic" : ""}`}>{n.t}</span>
      ))}
    </div>
    <div className="flex flex-wrap gap-2 mt-2">
      {buttons.map((b, i) => (
        <span key={i} className="text-xs px-3 py-1.5 rounded bg-[#1b2838] text-sky-300 border border-sky-800/40">{b}</span>
      ))}
    </div>
  </div>
);

export default function SendSignal() {
  const [f, setF] = useState({
    instrument: "Gold", order_type: "buy_above", entry: "", entry_lo: "", entry_hi: "",
    tp1: "", tp2: "", tp3: "", sl: "",
    free_show_tp: true, free_show_sl: false, ai_polish: false,
    targets: { free: true, paid: true, scalper: false },
    wa_free: false, wa_premium: false, push_app: true,
  });
  const [preview, setPreview] = useState({ free_text: "", paid_text: "" });
  const [channels, setChannels] = useState({});
  const [sending, setSending] = useState(false);
  const [media, setMedia] = useState(null);

  const payload = useCallback(() => ({
    instrument: f.instrument, order_type: f.order_type,
    entry: f.entry === "" ? null : Number(f.entry),
    entry_lo: f.entry_lo === "" ? null : Number(f.entry_lo),
    entry_hi: f.entry_hi === "" ? null : Number(f.entry_hi),
    tps: [f.tp1, f.tp2, f.tp3].filter((t) => t !== ""),
    sl: f.sl === "" ? null : Number(f.sl),
    free_show_tp: f.free_show_tp, free_show_sl: f.free_show_sl,
    ai_polish: false, targets: f.targets,
    wa_free: f.wa_free, wa_premium: f.wa_premium, push_app: f.push_app,
  }), [f]);

  useEffect(() => {
    const valid = isZone(f.order_type) ? (f.entry_lo && f.entry_hi) : f.entry;
    if (!valid) { setPreview({ free_text: "", paid_text: "" }); return; }
    const t = setTimeout(() => {
      client.post("/signals/manual-preview", payload())
        .then(({ data }) => { setPreview({ free_text: data.free_text, paid_text: data.paid_text }); setChannels(data.channels); })
        .catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [f, payload]);

  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const tgt = (k, v) => setF((s) => ({ ...s, targets: { ...s.targets, [k]: v } }));

  const send = async () => {
    if (!f.targets.free && !f.targets.paid && !f.targets.scalper && !f.wa_free && !f.wa_premium) { toast.error("Select at least one channel (Telegram or WhatsApp)"); return; }
    if (!preview.paid_text) { toast.error("Enter a valid entry price first"); return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("payload", JSON.stringify({ ...payload(), ai_polish: f.ai_polish }));
      if (media) fd.append("media", media);
      const { data } = await client.post("/signals/manual-send", fd);
      const parts = Object.entries(data.results || {}).map(([k, v]) => `${k}: ${v.ok ? "✓" : "✗ " + (v.error || "")}`);
      if (data.ok) { toast.success("Signal sent — " + parts.join("  ·  ")); setMedia(null); }
      else toast.error("Send failed — " + parts.join("  ·  "));
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    setSending(false);
  };

  const contact = channels.contact || "TFTdubai";
  const freeBtns = ["📈 Open Account", `💬 Contact Us → @${contact}`];
  const paidBtns = [`💬 Contact Us → @${contact}`];

  const inputCls = "w-full bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold focus:outline-none";
  const lblCls = "block text-xs text-slate-400 mb-1";

  return (
    <Layout title="Send Signal">
      <FreeKillSwitch />
      <p className="text-sm text-slate-400 mb-4">Manually publish a Gold / BTCUSD signal to your Free, Paid (VIP) & Scalper channels with the right format and buttons.</p>
      <div className="grid lg:grid-cols-2 gap-5">
        {/* Form */}
        <Panel title="Signal details" testid="ss-form">
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lblCls}>Instrument</label>
                <select value={f.instrument} onChange={(e) => upd("instrument", e.target.value)} data-testid="ss-instrument" className={inputCls}>
                  <option value="Gold">Gold (XAUUSD)</option><option value="BTC">BTC (BTCUSD)</option>
                </select>
              </div>
              <div><label className={lblCls}>Order type</label>
                <select value={f.order_type} onChange={(e) => upd("order_type", e.target.value)} data-testid="ss-order-type" className={inputCls}>
                  {ORDER_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </div>

            {isZone(f.order_type) ? (
              <div className="grid grid-cols-2 gap-3">
                <div><label className={lblCls}>Entry from</label><input type="number" value={f.entry_lo} onChange={(e) => upd("entry_lo", e.target.value)} data-testid="ss-entry-lo" className={inputCls} placeholder="e.g. 3338" /></div>
                <div><label className={lblCls}>Entry to</label><input type="number" value={f.entry_hi} onChange={(e) => upd("entry_hi", e.target.value)} data-testid="ss-entry-hi" className={inputCls} placeholder="e.g. 3342" /></div>
              </div>
            ) : (
              <div><label className={lblCls}>Entry price</label><input type="number" value={f.entry} onChange={(e) => upd("entry", e.target.value)} data-testid="ss-entry" className={inputCls} placeholder="e.g. 3340" /></div>
            )}

            <div className="grid grid-cols-3 gap-2">
              <div><label className={lblCls}>TP1</label><input type="number" value={f.tp1} onChange={(e) => upd("tp1", e.target.value)} data-testid="ss-tp1" className={inputCls} /></div>
              <div><label className={lblCls}>TP2</label><input type="number" value={f.tp2} onChange={(e) => upd("tp2", e.target.value)} data-testid="ss-tp2" className={inputCls} /></div>
              <div><label className={lblCls}>TP3</label><input type="number" value={f.tp3} onChange={(e) => upd("tp3", e.target.value)} data-testid="ss-tp3" className={inputCls} /></div>
            </div>
            <div><label className={lblCls}>Stop Loss (SL)</label><input type="number" value={f.sl} onChange={(e) => upd("sl", e.target.value)} data-testid="ss-sl" className={inputCls} placeholder="Required for Paid" /></div>

            <div className="border-t border-[color:var(--border-subtle)] pt-3 space-y-2">
              <div className="text-xs uppercase tracking-wider text-slate-500">Free channel options</div>
              <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={f.free_show_tp} onChange={(e) => upd("free_show_tp", e.target.checked)} data-testid="ss-free-tp" /> Show TP in free message</label>
              <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={f.free_show_sl} onChange={(e) => upd("free_show_sl", e.target.checked)} data-testid="ss-free-sl" /> Show SL in free message</label>
            </div>

            <div className="border-t border-[color:var(--border-subtle)] pt-3">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Attachment (optional)</div>
              <MediaPicker value={media} onChange={setMedia} testid="ss-media" />
            </div>

            <div className="border-t border-[color:var(--border-subtle)] pt-3 space-y-2">
              <div className="text-xs uppercase tracking-wider text-slate-500">Send to</div>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={f.targets.free} onChange={(e) => tgt("free", e.target.checked)} data-testid="ss-target-free" /> Free</label>
                <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={f.targets.paid} onChange={(e) => tgt("paid", e.target.checked)} data-testid="ss-target-paid" /> Paid (VIP)</label>
                <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={f.targets.scalper} onChange={(e) => tgt("scalper", e.target.checked)} data-testid="ss-target-scalper" /> Scalper</label>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-300 pt-1"><input type="checkbox" checked={f.ai_polish} onChange={(e) => upd("ai_polish", e.target.checked)} data-testid="ss-ai-polish" /> Polish wording with AI before sending</label>
              <div className="pt-2 mt-1 border-t border-[color:var(--border-subtle)]">
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#25D366]" /> Also send to WhatsApp</div>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={f.wa_free} onChange={(e) => upd("wa_free", e.target.checked)} data-testid="ss-wa-free" /> WhatsApp Free</label>
                  <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={f.wa_premium} onChange={(e) => upd("wa_premium", e.target.checked)} data-testid="ss-wa-premium" /> WhatsApp Premium</label>
                  <label className="flex items-center gap-2 text-sm text-gold"><input type="checkbox" checked={f.push_app} onChange={(e) => upd("push_app", e.target.checked)} data-testid="ss-push-app" /> 📱 App notification</label>
                </div>
              </div>
            </div>

            <button onClick={send} disabled={sending} data-testid="ss-send" className="btn-gold w-full py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} {sending ? "Sending…" : "Send Signal"}
            </button>
          </div>
        </Panel>

        {/* Preview */}
        <div className="space-y-4">
          <Bubble title="Free channel preview" text={preview.free_text || "— enter entry price to preview —"} buttons={preview.free_text ? freeBtns : []} testid="ss-preview-free" />
          <Bubble title="Paid (VIP) channel preview" text={preview.paid_text || "— enter entry price to preview —"} buttons={preview.paid_text ? paidBtns : []} testid="ss-preview-paid" />
          <p className="text-[11px] text-slate-600">Scalper channel receives the same full message as Paid, with a Help &amp; Support button.</p>
        </div>
      </div>
    </Layout>
  );
}
